import { ResponseError } from 'types'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiErrorLink {
  link: string
  name: string
  type: string
}

export enum ApiErrorCodes {
  ACCOUNT_NOT_VERIFIED = 'account_not_verified',
  ACCOUNT_DEACTIVATED = 'account_deactivated',
  SUBSCRIPTION_LEVEL = 'subscription_level_error',
  TWO_FACTOR_REQUIRED = 'two_factor_required',
  REQUEST_PARAMS_ERROR = 'request_params_error',
  UNAUTHORIZED = 'unauthorized',
  UNKNOWN = 'unknown',
  RESOURCE_IN_USE = 'resource_in_use',
  TOO_MANY_REQUESTS = 'too_many_requests',
  DUPLICATED_VALUE = 'duplicated_value'
}

export class APIError extends Error {
  code: ApiErrorCodes
  rawCode: number
  message: string
  errors?: ResponseError['detail']['errors']
  path?: string
  method?: HttpMethod
  links?: ApiErrorLink[]
  needsSupport?: boolean
  constructor(
    code: ApiErrorCodes,
    rawCode: number,
    message: string,
    errors?: ResponseError['detail']['errors'],
    path?: string,
    method?: HttpMethod,
    links?: ApiErrorLink[],
    needsSupport?: boolean
  ) {
    super()
    this.message = message
    this.code = code
    this.errors = errors
    this.rawCode = rawCode
    this.path = path
    this.method = method
    this.links = links
    this.needsSupport = needsSupport
  }
}

/**
 * Whether the error modal should offer AI Support for a failed request. Errors the API answers
 * with a message the user can act on (a duplicate value, a missing permission) show just that
 * message; support is for faults, failures with no explanation, and errors the API flags as
 * needing Morta to diagnose.
 *
 * Reads the error structurally for the same reason as getApiErrorMessage below.
 */
export function shouldOfferSupport(error: Pick<APIError, 'code' | 'rawCode' | 'needsSupport'>): boolean {
  return (
    Boolean(error.needsSupport) || error.rawCode === 0 || error.rawCode >= 500 || error.code === ApiErrorCodes.UNKNOWN
  )
}

/**
 * The message to show a user for a failed request, for the cases where the error belongs next to
 * the field that caused it rather than in the global snackbar.
 *
 * Reads `message` structurally rather than through `instanceof APIError`: Vitest aliases
 * `helpers/api` to a mock that declares its own APIError, so the prototype check fails under test
 * even when the error is the real thing.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown } | null | undefined)?.message
  return typeof message === 'string' && message.trim() !== '' ? message : fallback
}

/** Preserve support diagnostics when a caller adds resource-specific error context. */
export function getErrorSupportMetadata(error: unknown) {
  if (error && typeof error === 'object' && 'rawCode' in error && 'code' in error) {
    const apiError = error as APIError
    return { offerSupport: shouldOfferSupport(apiError), path: apiError.path, method: apiError.method }
  }
  return { offerSupport: typeof error !== 'string' }
}
